import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./rules.css";
import { fetchThemeData } from "../../themeSlice";
import { processQuestions } from "../../questionSlice";
import { selectAdminToken } from "../../sessionSlice";
import { useNavigate } from "react-router-dom";
import { updateThemeData } from "../../functions/updateThemeData";

const GRID_OPTIONS = ["3x3", "4x4", "5x5", "6x6", "7x7", "8x8"];
const TOTAL_MIN_SEC = 180;
const TOTAL_MAX_SEC = 600;
const START_MIN_SEC = 60;
const DURATION_MIN_SEC = 30;
const DURATION_MAX_SEC = 120;

function normalizeTime(value, fallback = "00:00") {
  const text = String(value || "").trim();
  const match = text.match(/^(\d{1,2}):(\d{1,2})$/);
  if (!match) return fallback;
  const mm = Math.max(0, Math.min(99, Number(match[1]) || 0));
  const ss = Math.max(0, Math.min(59, Number(match[2]) || 0));
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

function timeToSeconds(value, fallback = 0) {
  const safe = normalizeTime(value, "00:00");
  const [m, s] = safe.split(":").map((n) => Number(n) || 0);
  const total = m * 60 + s;
  return Number.isFinite(total) ? total : fallback;
}

function secondsToTime(total) {
  const safe = Math.max(0, Math.floor(Number(total) || 0));
  const mm = Math.floor(safe / 60);
  const ss = safe % 60;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector(
    (state) => state.theme
  );

  const [rules, setRules] = useState([""]);
  const [gridSize, setGridSize] = useState("6x6");
  const [totalTime, setTotalTime] = useState("10:00");
  const [votingStartTime, setVotingStartTime] = useState("08:00");
  const [votingDuration, setVotingDuration] = useState("01:00");

  useEffect(() => {
    dispatch(processQuestions());
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    setRules(data.rules || [""]);
    setGridSize(GRID_OPTIONS.includes(String(data.grid_size)) ? String(data.grid_size) : "6x6");
    const nextTotal = Math.max(TOTAL_MIN_SEC, Math.min(TOTAL_MAX_SEC, timeToSeconds(data.total_time, TOTAL_MAX_SEC)));
    const nextStart = Math.max(START_MIN_SEC, Math.min(nextTotal, timeToSeconds(data.voting_start_time, nextTotal)));
    const nextDuration = Math.max(
      DURATION_MIN_SEC,
      Math.min(DURATION_MAX_SEC, timeToSeconds(data.voting_duration, 60))
    );
    setTotalTime(secondsToTime(nextTotal));
    setVotingStartTime(secondsToTime(nextStart));
    setVotingDuration(secondsToTime(nextDuration));
  }, [data]);

  const handleRuleChange = (idx, val) => {
    let newVal = val;
    if (val.length > 100) {
      Swal.fire(
        "Limit Exceeded",
        "Rules can be at most 100 characters. extra text has been trimmed.",
        "warning"
      );
      newVal = val.slice(0, 100);
    }
    const arr = [...rules];
    arr[idx] = newVal;
    setRules(arr);
  };

  const handleAddRule = () => {
    if (rules.length >= 6) {
      return Swal.fire(
        "Limit Reached",
        "You can only add up to 6 rules.",
        "warning"
      );
    }
    setRules([...rules, ""]);
  };
  const handleRemoveRule = (idx) => {
    if (rules.length <= 1) {
      return Swal.fire(
        "At Least One Rule Required",
        "You must have at least one rule.",
        "warning"
      );
    }
    setRules(rules.filter((_, i) => i !== idx));
  };

  const updateTimePart = (field, part, value) => {
    const source = field === "total" ? totalTime : field === "start" ? votingStartTime : votingDuration;
    const [m, s] = normalizeTime(source).split(":");
    const parsed = Number(value);
    const safe = Number.isFinite(parsed) ? parsed : 0;
    const nextPart = part === "mm"
      ? String(Math.max(0, Math.min(99, safe))).padStart(2, "0")
      : String(Math.max(0, Math.min(59, safe))).padStart(2, "0");
    const nextRaw = part === "mm" ? `${nextPart}:${s}` : `${m}:${nextPart}`;

    const currentTotalSec = timeToSeconds(totalTime, TOTAL_MAX_SEC);
    if (field === "total") {
      const nextTotalSec = Math.max(TOTAL_MIN_SEC, Math.min(TOTAL_MAX_SEC, timeToSeconds(nextRaw, TOTAL_MAX_SEC)));
      setTotalTime(secondsToTime(nextTotalSec));
      setVotingStartTime((prev) => {
        const prevSec = timeToSeconds(prev, nextTotalSec);
        return secondsToTime(Math.max(START_MIN_SEC, Math.min(nextTotalSec, prevSec)));
      });
      return;
    }
    if (field === "start") {
      const nextStartSec = Math.max(START_MIN_SEC, Math.min(currentTotalSec, timeToSeconds(nextRaw, currentTotalSec)));
      setVotingStartTime(secondsToTime(nextStartSec));
      return;
    }
    const nextDurationSec = Math.max(
      DURATION_MIN_SEC,
      Math.min(DURATION_MAX_SEC, timeToSeconds(nextRaw, 60))
    );
    setVotingDuration(secondsToTime(nextDurationSec));
  };

  const handleSubmit = async () => {
    const payload = {
      data: {
        rules: rules.filter((r) => r.trim() !== ""),
        grid_size: gridSize,
        total_time: normalizeTime(totalTime, "10:00"),
        voting_start_time: normalizeTime(votingStartTime, "08:00"),
        voting_duration: normalizeTime(votingDuration, "01:00"),
      },
      currentTheme,
    };

    try {
      await updateThemeData({ payload, token: adminToken });
      // Swal.fire("Success", "Settings saved successfully!", "success");
      setTimeout(() => navigate("/admin/questions"), 500);
    } catch (e) {
      Swal.fire(
        "Error",
        e.message || "Failed to save. Please try again.",
        "error"
      );
    }
  };

  const timeField = (label, value, fieldKey) => {
    const [mm, ss] = normalizeTime(value).split(":");
    return (
      <div className="form-group-v1 rules-time-field">
        <label>{label}</label>
        <div className="rules-mmss-row">
          <input
            type="number"
            min="0"
            max="99"
            value={Number(mm)}
            onChange={(e) => updateTimePart(fieldKey, "mm", e.target.value)}
          />
          <span>:</span>
          <input
            type="number"
            min="0"
            max="59"
            value={Number(ss)}
            onChange={(e) => updateTimePart(fieldKey, "ss", e.target.value)}
          />
          <small>MM : SS</small>
        </div>
      </div>
    );
  };

  return (
    <>

      <div className="rules-page-layout">
        <div className="back-button-holder">
          <button
            className="back-button"
            onClick={() => navigate("/admin/")}
          >
            <i class="fa-solid fa-arrow-left"></i> Back
          </button>
        </div>
        <div className="rules-page-new">
        <div className="admin-tabs-holder">
          <div className="admin-tab-container">
            <div className="admin-tab-title">
              <i className="fa-solid fa-sliders"></i> &nbsp; Basic Customization
            </div>
            <div className="form-group-v1">
              <label>Grid Size</label>
              <select
                className="rules-grid-select"
                value={gridSize}
                onChange={(e) => setGridSize(e.target.value)}
              >
                {GRID_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            {timeField("Total Time", totalTime, "total")}
            {timeField("Voting Start Time", votingStartTime, "start")}
            {timeField("Voting Duration", votingDuration, "duration")}
          </div>

          <div className="admin-tab-container">
            <div className="admin-tab-title">
              <i class="fa-solid fa-trophy"></i> &nbsp; Game Rules
            </div>
            <h3 className="custom-rules-title">Custom rules</h3>
            {rules.map((rule, i) => (
              <div className="rule-row" key={i}>
                <span className="rule-number">{i + 1}.</span>
                <input
                  className="rule-input"
                  type="text"
                  placeholder="Enter rule"
                  value={rule}
                  onChange={(e) => handleRuleChange(i, e.target.value)}
                />
                <button
                  className="remove-btn"
                  onClick={() => handleRemoveRule(i)}
                >
                  &times;
                </button>
              </div>
            ))}
            <div className="rules-button-container">
              <div>
                <button className="add-btn" onClick={handleAddRule}>
                  + Add New Rule
                </button>
              </div>
              <div className="code">
                * Keep 6 rules at the most. <br></br>* At least 1 rule is
                required.<br></br>* character limit of each rule is 100<br></br>
              </div>
            </div>
          </div>

          <div className="rules-action-holder">
            <button className="save-and-continue" onClick={handleSubmit}>
              Save & Continue
            </button>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default Rules;
